import { useEffect, useState } from "react";
import { Plus, Pencil, MapPin, Star, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import ConfirmAction from "@/components/ConfirmAction";

const emptyForm = {
  label: "", address_line1: "", address_line2: "", city: "", state: "", zip: "",
  site_contact_name: "", site_contact_phone: "", site_manager: "", instructions: "", is_default: false, kind: "",
};

export default function DeliveryAddresses() {
  const [addresses, setAddresses] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [removing, setRemoving] = useState(null);

  const load = () => api.get("/contractor/delivery-addresses", { params: { include_inactive: true } }).then((r) => setAddresses(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShow(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      label: a.label, address_line1: a.address_line1, address_line2: a.address_line2 || "",
      city: a.city || "", state: a.state || "", zip: a.zip || "",
      site_contact_name: a.site_contact_name || "", site_contact_phone: a.site_contact_phone || "",
      site_manager: a.site_manager || "", instructions: a.instructions || "", is_default: a.is_default || false, kind: a.kind || "",
    });
    setShow(true);
  };

  const save = async () => {
    if (!form.label || !form.address_line1) return toast.error("Label and address line 1 required");
    try {
      if (editing) {
        await api.patch(`/contractor/delivery-addresses/${editing.id}`, form);
        toast.success("Delivery address updated");
      } else {
        await api.post("/contractor/delivery-addresses", form);
        toast.success("Delivery address created");
      }
      setShow(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save address");
    }
  };

  const remove = async () => {
    const { data } = await api.delete(`/contractor/delivery-addresses/${removing.id}`);
    toast.success(data.kept_for_history
      ? "Deleted — past purchases that used it still show it. Restore it from Recently Deleted."
      : "Deleted — restore it from Recently Deleted.");
    load();
  };

  const toggleActive = async (a) => {
    await api.patch(`/contractor/delivery-addresses/${a.id}/active`, { active: !a.active });
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="delivery-addresses-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Delivery Addresses</h1>
          <p className="text-slate-500 text-sm mt-1">Your company's own addresses — only your company sees them. Edit or delete any of them at any time.</p>
        </div>
        <button onClick={openNew} data-testid="new-address-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
          <Plus size={14} /> New Address
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-7" data-testid="delivery-addresses-grid">
        {addresses.map((a) => (
          <div key={a.id} className={`border border-slate-200 rounded-xl p-4 flex items-start gap-3 ${!a.active ? "opacity-50" : ""}`} data-testid={`address-card-${a.id}`}>
            <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center text-sky-600 shrink-0"><MapPin size={16} /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[14px] flex items-center gap-1.5">
                  {a.label}
                  {a.kind && <span className="ss-pill ss-pill-slate capitalize">{a.kind}</span>}
                  {a.source === "quickbooks" && <span className="ss-pill ss-pill-slate">From QuickBooks</span>}
                  {a.is_default && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`default-badge-${a.id}`}>
                      <Star size={9} className="fill-amber-500 text-amber-500" /> Default
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => openEdit(a)} data-testid={`edit-address-${a.id}`} title="Edit" className="text-slate-400 hover:text-sky-600"><Pencil size={14} /></button>
                  <button onClick={() => setRemoving(a)} data-testid={`delete-address-${a.id}`} title="Delete" className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="text-[12.5px] text-slate-500 mt-1">
                {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ""}<br />
                {[a.city, a.state, a.zip].filter(Boolean).join(", ")}
              </div>
              {(a.site_contact_name || a.site_manager) && (
                <div className="text-[11.5px] text-slate-400 mt-1.5">
                  {a.site_contact_name && <>Contact: {a.site_contact_name} {a.site_contact_phone && `· ${a.site_contact_phone}`}</>}
                  {a.site_manager && <div>Site manager: {a.site_manager}</div>}
                </div>
              )}
              {a.instructions && <div className="text-[11.5px] text-slate-400 mt-1">{a.instructions}</div>}
              <button onClick={() => toggleActive(a)} data-testid={`toggle-address-${a.id}`} className="text-[12px] font-semibold text-sky-600 mt-2">
                {a.active ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        ))}
        {addresses.length === 0 && <div className="text-slate-400 text-sm col-span-2 py-10 text-center">No delivery addresses yet.</div>}
        <ConfirmAction open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title={`Delete ${removing?.label}?`}
          description="It's removed from your list and every job, and kept in Recently Deleted so you can restore it. Past purchases delivered there keep showing the address."
          confirmLabel="Delete address" onConfirm={remove} testId="delete-address-dialog" />
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="address-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Delivery Address" : "New Delivery Address"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (e.g. Main Office)" data-testid="address-label-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} placeholder="Address line 1" data-testid="address-line1-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} placeholder="Address line 2 (optional)" data-testid="address-line2-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <div className="grid grid-cols-3 gap-2">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" data-testid="address-city-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" data-testid="address-state-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="ZIP" data-testid="address-zip-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={form.site_contact_name} onChange={(e) => setForm({ ...form, site_contact_name: e.target.value })} placeholder="Site contact name" data-testid="address-contact-name-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input value={form.site_contact_phone} onChange={(e) => setForm({ ...form, site_contact_phone: e.target.value })} placeholder="Site contact phone" data-testid="address-contact-phone-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <input value={form.site_manager} onChange={(e) => setForm({ ...form, site_manager: e.target.value })} placeholder="Site manager (optional)" data-testid="address-site-manager-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Delivery instructions (optional)" data-testid="address-instructions-input" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" rows={2} />
            <div>
              <label className="text-[12.5px] text-slate-500">Type</label>
              <SearchableSelect value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="address-kind-select" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                <option value="">Other</option>
                <option value="office">Office</option>
                <option value="warehouse">Warehouse</option>
              </SearchableSelect>
              <p className="text-[11.5px] text-slate-400 mt-1">Office and warehouse addresses are offered on Expense Log entries. Purchase orders deliver to the job site or your main office — the default address.</p>
            </div>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="address-is-default-checkbox" />
              Set as default address
            </label>
            <button onClick={save} data-testid="save-address-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">{editing ? "Save Changes" : "Create Address"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
